import React, { useState } from 'react';
import { Search, SlidersHorizontal, X, ArrowUpDown } from 'lucide-react';

export default function FilterBar({
  searchQuery,
  onSearchChange,
  selectedGender,
  onGenderChange,
  selectedSize,
  onSizeChange,
  selectedColor,
  onColorChange,
  sortBy,
  onSortChange,
  onClearFilters,
  hasActiveFilters
}) {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const sizes = ['P', 'M', 'G', 'GG', 'XG', 'Único', '35-38', '39-43'];
  const colors = [
    { name: 'Preto Stealth', hex: '#0d120f' },
    { name: 'Grafite Carbon', hex: '#1b221e' },
    { name: 'Verde Neon Flash', hex: '#b0ff00' },
    { name: 'Branco', hex: '#ffffff' }
  ];

  return (
    <div className="space-y-3">
      {/* Barra Principal: Busca, Gênero, Ordenação e Botão Filtros */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3 bg-urb-card/60 p-3 rounded-2xl border border-urb-border">
        
        {/* Campo de Busca */}
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-urb-text-muted absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Buscar por camisa, boné, meia, kit..."
            className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-urb-dark/80 border border-urb-border text-sm text-white placeholder-urb-text-muted focus:outline-none focus:border-urb-primary transition-colors"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => onSearchChange('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-urb-text-muted hover:text-white"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Controles de Gênero e Ordenação */}
        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
          
          {/* Seletor de Gênero */}
          <div className="flex items-center bg-urb-dark/80 rounded-xl p-1 border border-urb-border shrink-0">
            {['Todos', 'Masculino', 'Feminino'].map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => onGenderChange(g)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  selectedGender === g
                    ? 'bg-urb-primary text-black'
                    : 'text-urb-text-secondary hover:text-white'
                }`}
              >
                {g}
              </button>
            ))}
          </div>

          {/* Ordenação */}
          <div className="relative shrink-0">
            <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-urb-dark/80 border border-urb-border text-xs text-white">
              <ArrowUpDown className="w-3.5 h-3.5 text-urb-primary" />
              <select
                value={sortBy}
                onChange={(e) => onSortChange(e.target.value)}
                className="bg-transparent text-xs text-white font-bold focus:outline-none cursor-pointer pr-1"
              >
                <option value="relevance" className="bg-urb-dark text-white">Mais Relevantes</option>
                <option value="bestsellers" className="bg-urb-dark text-white">Mais Vendidos</option>
                <option value="price_asc" className="bg-urb-dark text-white">Menor Preço</option>
                <option value="price_desc" className="bg-urb-dark text-white">Maior Preço</option>
                <option value="newest" className="bg-urb-dark text-white">Lançamentos</option>
              </select>
            </div>
          </div>

          {/* Botão de Filtros Avançados */}
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold border transition-colors shrink-0 cursor-pointer ${
              showAdvanced || hasActiveFilters
                ? 'bg-urb-primary/10 border-urb-primary text-urb-primary'
                : 'bg-urb-dark/80 border-urb-border text-urb-text-secondary hover:text-white'
            }`}
          >
            <SlidersHorizontal className="w-3.5 h-3.5" />
            <span>Filtros</span>
            {hasActiveFilters && (
              <span className="w-2 h-2 rounded-full bg-urb-primary" />
            )}
          </button>

        </div>

      </div>

      {/* Painel de Filtros Avançados Expansível */}
      {showAdvanced && (
        <div className="p-4 rounded-2xl bg-urb-card border border-urb-border space-y-4 animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-4 items-center">
            
            {/* Tamanhos */}
            <div className="lg:col-span-7">
              <span className="text-[11px] font-black uppercase text-urb-text-muted tracking-wider block mb-2">
                Tamanhos Disponíveis
              </span>
              <div className="flex flex-wrap gap-1.5">
                {sizes.map((s) => {
                  const isSel = selectedSize === s;
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => onSizeChange(isSel ? null : s)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-black transition-all ${
                        isSel
                          ? 'bg-urb-primary text-black border border-urb-primary'
                          : 'bg-urb-dark border border-urb-border text-urb-text-secondary hover:text-white hover:border-urb-primary/30'
                      }`}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Cores */}
            <div className="lg:col-span-5">
              <span className="text-[11px] font-black uppercase text-urb-text-muted tracking-wider block mb-2">
                Cores do Drop
              </span>
              <div className="flex flex-wrap gap-2">
                {colors.map((c) => {
                  const isSel = selectedColor === c.name;
                  return (
                    <button
                      key={c.name}
                      type="button"
                      onClick={() => onColorChange(isSel ? null : c.name)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                        isSel
                          ? 'bg-white/15 text-white border border-urb-primary'
                          : 'bg-urb-dark border border-urb-border text-urb-text-secondary hover:text-white'
                      }`}
                    >
                      <span
                        className="w-3 h-3 rounded-full border border-white/20 shrink-0"
                        style={{ backgroundColor: c.hex }}
                      />
                      <span>{c.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>

          </div>

          {/* Botão de Limpar Filtros */}
          {hasActiveFilters && (
            <div className="pt-2 border-t border-urb-border/50 flex justify-end">
              <button
                type="button"
                onClick={onClearFilters}
                className="inline-flex items-center gap-1.5 text-xs text-urb-primary hover:underline font-bold cursor-pointer"
              >
                <X className="w-3 h-3" />
                <span>Limpar todos os filtros</span>
              </button>
            </div>
          )}

        </div>
      )}
    </div>
  );
}
